import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import ReactTooltip from 'react-tooltip'
import moment from 'moment'

import { getCommunityPackages } from '../../../../actions/packagesActions'
import { currencySymbol, displayWithCurrency } from '../../../../utility/currency'
import {
  DISCOUNT_TYPES, discountDescriptionFor, invoiceOfflinePaymentDescription, parseDiscountEntry, resolveDiscount,
} from '../../../../utility/enrollmentsBilling'
import Modal from '../../../common/Modal'
import Tooltip from '../../../common/Tooltip'
import SubmitButton from '../../../common/buttons/SubmitButton'
import ProfileAvatar from '../../../common/avatar/ProfileAvatar'
import Select from '../../../common/form/Select'
import Input from '../../../common/form/Input'

import { ReactComponent as InfoCircleIcon } from '../../../../assets/images/common/icons/info-circle-icon-dark.svg'
import classes from './CreateEnrollmentModal.module.scss'

// Ordered for a row-major 2-column grid so the left column is Cash/Checks and the
// right column is Member Charge/Custom, matching the design. Labels are translated
// inside the components (see offlineMethods) — this holds the stable value order only.
const OFFLINE_METHOD_VALUES = ['cash', 'member_charge', 'check', 'custom']

// Sentinel for the explicit "No Package" selector option — enroll with no invoice. Distinct from an
// empty selection (nothing chosen yet), which shows only the package selector.
const NO_PACKAGE = 'none'

// Short offline-method label used in the invoice preview's payment-method line.
const offlineMethodLabel = (t, value) => {
  switch (value) {
    case 'member_charge': return t('enrollmentsBilling.createModal.methodMemberCharge')
    case 'check': return t('enrollmentsBilling.createModal.methodCheck')
    case 'custom': return t('enrollmentsBilling.createModal.methodCustomShort')
    default: return t('enrollmentsBilling.createModal.methodCash')
  }
}

const money = (amount, currency) => (currency ? displayWithCurrency(amount, currency) : `$${Number(amount || 0).toFixed(2)}`)

// Ensure the enrollment's already-assigned package is selectable even when it's no longer among the
// course's available packages (archived, or an enrollment created before invoicing existed).
const withEnrollmentPackage = (packages, enrollmentPackage) => {
  if (!enrollmentPackage || packages.some(pkg => String(pkg.id) === String(enrollmentPackage.id))) {
    return packages
  }
  return [...packages, enrollmentPackage]
}

// Seed the form. Edit pre-fills every field from the existing invoice/enrollment.
const getInitialFormState = ({ isEdit, invoice, packages, enrollment, stripeAccountConnected }) => {
  const items = invoice?.lineItems || []
  const chargeItem = items.find(item => item.lineType !== 'discount')
  const discountItem = items.find(item => item.lineType === 'discount')

  if (!isEdit) {
    return {
      packageId: '', discountAmount: '', discountType: DISCOUNT_TYPES.fixed, discountDescription: '', markAsPaid: true,
      onlineSelected: false, offlineSelected: true, offlineMethod: 'cash', customDescription: '', coachMessage: '', sendEmail: true,
    }
  }

  const matched = packages.find(pkg => pkg.name === chargeItem?.description)
  const offline = OFFLINE_METHOD_VALUES.includes(invoice?.paymentMethod) ? invoice.paymentMethod : 'cash'
  const online = invoice?.paymentMethod === 'online'
  const unspecified = !invoice?.paymentMethod
  return {
    packageId: matched?.id ?? enrollment?.package?.id ?? '',
    ...parseDiscountEntry(discountItem),
    discountDescription: discountItem?.description || '',
    markAsPaid: false,
    // An outstanding invoice with no committed method shows both selectable options checked -
    // but only where a Stripe account is connected, since online is unusable without one.
    onlineSelected: online || (unspecified && stripeAccountConnected),
    offlineSelected: !online,
    offlineMethod: offline,
    customDescription: invoiceOfflinePaymentDescription(invoice) || '',
    coachMessage: invoice?.notes || '',
    sendEmail: true,
  }
}

// Builds the onSubmit payload per mode. Edit rebuilds the invoice's line items from the chosen
// package + discount; the update endpoint replaces the full set and recomputes the total server-side.
const resolvePaymentMethod = ({ markAsPaid, onlineSelected, offlineSelected, offlineMethod, stripeAccountConnected }) => {
  if (!markAsPaid && !stripeAccountConnected && offlineSelected && !onlineSelected) { return null }
  if (markAsPaid || (offlineSelected && !onlineSelected)) { return offlineMethod }
  if (onlineSelected && !offlineSelected) { return 'online' }

  // Both (or neither) selected on an outstanding invoice — the method is set when it's paid.
  return null
}

const buildSubmitPayload = ({
  mode, onlineSelected, offlineSelected, offlineMethod, customDescription, sendEmail, selectedPackage, packagePrice,
  discount, discountDescription, coachMessage, chargeItem, enrollment, packageId, markAsPaid, stripeAccountConnected,
}) => {
  const paymentMethod = resolvePaymentMethod({ markAsPaid, onlineSelected, offlineSelected, offlineMethod, stripeAccountConnected })
  const offlinePaymentDescription = paymentMethod === 'custom' ? customDescription : null

  if (mode === 'markAsPaid') {
    return { paymentMethod, offlinePaymentDescription, sendEmail }
  }

  if (mode === 'edit') {
    const lineItems = []
    if (selectedPackage) {
      lineItems.push({
        lineType: 'charge',
        billableType: chargeItem?.billableType || 'Enrollment',
        billableId: chargeItem?.billableId ?? enrollment?.id,
        description: selectedPackage.name,
        amount: packagePrice,
        quantity: 1,
      })
    }
    if (discount > 0) {
      lineItems.push({ lineType: 'discount', description: discountDescription || 'Discount', amount: -discount, quantity: 1 })
    }
    // markAsPaid drives a chained mark-as-paid after the edit is saved (see useInvoiceActions).
    // packageId keeps the enrollment's package association in sync with the charge (server-side).
    return {
      packageId: selectedPackage?.id ?? null,
      paymentMethod, offlinePaymentDescription, notes: coachMessage, lineItems, markAsPaid, sendEmail,
    }
  }

  // "No Package" (or nothing) selected: enroll with no invoice — only the coach message + email flag.
  if (!selectedPackage) {
    return {
      packageId: null, discountAmount: null, discountDescription: null,
      paymentMethod: null, offlinePaymentDescription: null, markAsPaid: false, coachMessage, sendEmail,
    }
  }

  return {
    packageId,
    discountAmount: discount > 0 ? discount : null,
    discountDescription: discount > 0 ? (discountDescription || 'Discount') : null,
    paymentMethod,
    offlinePaymentDescription,
    markAsPaid,
    coachMessage,
    sendEmail,
  }
}

// Left-form visibility + submit-disabled state derived from the current selection. Kept out of the
// component to bound its cyclomatic complexity. Create and Edit share three states: nothing selected
// → only the selector; "No Package" → notes/email; a real package → full billing. Mark-as-Paid acts
// on an existing paid-in-full invoice, so it always shows everything.
const deriveFormState = ({ form, isLoading, isMarkAsPaid, selectedPackage, isCustom, canBill }) => {
  const showBilling = canBill && (isMarkAsPaid || !!selectedPackage)
  const showNotes = isMarkAsPaid || form.packageId !== ''
  // Only a truly empty selection blocks submit; "No Package" is a valid choice in both Create and Edit.
  const nothingSelected = !isMarkAsPaid && form.packageId === ''
  const disabled = isLoading || nothingSelected || (isCustom && showBilling && !form.customDescription.trim())
  return { showBilling, showNotes, disabled }
}

// Create Enrollment modal (Figma 1985-13012). Three modes:
//   create      — billing step of the coach add-students flow
//   markAsPaid  — reused to mark an existing invoice paid (payment method only)
//   edit        — full edit of an outstanding invoice (package/discount/method/notes + optional mark paid)
const CreateEnrollmentModal = ({
  isOpen, onClose, mode = 'create', program, students = [], enrollment, onSubmit, isLoading, fromWaitlist = false,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const community = useSelector(state => state.user.current_user.communities[0])
  const billingEnabled = community?.has_billing_enabled !== false
  const stripeAccountConnected = !!community?.selected_stripe_connected_account
  const communityPackages = useSelector(state => state.programBuilder.communityPackages)
  const isMarkAsPaid = mode === 'markAsPaid'
  const isEdit = mode === 'edit'
  const forExistingInvoice = isMarkAsPaid || isEdit
  const canBill = billingEnabled || forExistingInvoice
  const invoice = enrollment?.invoice
  const course = forExistingInvoice ? enrollment?.course : program

  useEffect(() => {
    dispatch(getCommunityPackages())
  }, [dispatch])

  useEffect(() => {
    if (isOpen) { ReactTooltip.rebuild() }
  }, [isOpen, stripeAccountConnected])

  const packages = withEnrollmentPackage(communityPackages || [], enrollment?.package)
  const chargeItem = (invoice?.lineItems || []).find(item => item.lineType !== 'discount')

  // One local form-state object keeps the field-props to EnrollmentForm to a handful instead of ~20.
  const [form, setForm] = useState(() => getInitialFormState({ isEdit, invoice, packages, enrollment, stripeAccountConnected }))
  const update = patch => setForm(current => ({ ...current, ...patch }))

  // The community carries a full currency object (code, symbol, zero-decimal); an invoice carries
  // only its code. Prefer the object - falling back to a bare code loses the rest - and use the
  // invoice's code only when it was billed in something other than the community's currency.
  const communityCurrency = community?.currency
  const invoiceCurrencyCode = forExistingInvoice ? invoice?.currencyCode : null
  const currency = invoiceCurrencyCode && invoiceCurrencyCode !== communityCurrency?.code
    ? { code: invoiceCurrencyCode }
    : communityCurrency
  const selectedPackage = packages.find(pkg => String(pkg.id) === String(form.packageId))
  const packagePrice = Number(selectedPackage?.price || 0)
  // Three left-form states in create: nothing selected → only the package selector; "No Package" →
  // selector + notes/email (no invoice); a real package → the full billing fields. Mark-as-Paid/Edit
  // always act on an existing invoice, so they show everything.
  const noPackage = !canBill || (!isMarkAsPaid && !selectedPackage)
  const discount = resolveDiscount(form, packagePrice)
  const isCustom = form.offlineSelected && form.offlineMethod === 'custom'
  // Online is selectable whenever the charge is Outstanding and Stripe is connected; Mark as Paid
  // forces a single Offline method.
  const canChooseMethod = !isMarkAsPaid && !form.markAsPaid
  const { showBilling, showNotes, disabled } = deriveFormState({
    form, isLoading, isMarkAsPaid, selectedPackage, isCustom, canBill,
  })

  const handleSubmit = () => onSubmit(buildSubmitPayload({
    mode, ...form, isCustom, selectedPackage, packagePrice, discount, chargeItem, enrollment, stripeAccountConnected,
  }))

  return (
    <Modal isOpen={isOpen} onClose={onClose} className={classes.modal}>
      <div className={classes.layout}>
        <EnrollmentForm
          form={form}
          update={update}
          isMarkAsPaid={isMarkAsPaid}
          isEdit={isEdit}
          invoice={invoice}
          currency={currency}
          packages={packages}
          canChooseMethod={canChooseMethod}
          stripeAccountConnected={stripeAccountConnected}
          isCustom={isCustom}
          showBilling={showBilling}
          showNotes={showNotes}
          resolvedDiscount={discount}
          fromWaitlist={fromWaitlist}
        />

        <InvoicePreview
          isMarkAsPaid={isMarkAsPaid}
          forExistingInvoice={forExistingInvoice}
          noPackage={noPackage}
          students={students}
          course={course}
          community={community}
          coach={program?.coaches?.[0]}
          invoice={invoice}
          currency={currency}
          onlineSelected={form.onlineSelected}
          offlineSelected={form.offlineSelected}
          offlineMethod={form.offlineMethod}
          packageName={chargeItem?.description || selectedPackage?.name}
          packagePrice={packagePrice}
          discount={discount}
          discountLabel={form.discountDescription}
          coachMessage={form.coachMessage}
        />
      </div>

      <div className={classes.footer}>
        <SubmitButton
          buttonMessage={forExistingInvoice || fromWaitlist
            ? t('enrollmentsBilling.createModal.confirm')
            : t('enrollmentsBilling.createModal.createEnrollment')}
          buttonColor='orange-button'
          className={classes.submitButton}
          disabled={disabled}
          loading={isLoading}
          onClick={handleSubmit}
        />
      </div>
    </Modal>
  )
}

// Left column: the editable form. Package/discount are read-only in Mark-as-Paid mode.
const EnrollmentForm = ({
  form, update, isMarkAsPaid, isEdit, invoice, currency, packages, canChooseMethod, stripeAccountConnected, isCustom, showBilling, showNotes,
  resolvedDiscount, fromWaitlist,
}) => {
  const { t } = useTranslation()
  const symbol = currencySymbol(currency)
  const canChooseOnline = canChooseMethod && stripeAccountConnected
  const updateDiscount = (patch) => {
    const next = { ...form, ...patch }
    update({ ...patch, discountDescription: discountDescriptionFor(next), discountAmount: String(next.discountAmount ?? '') })
  }
  // Coming from a waitlist the student already has a place in the queue, so this step moves them
  // into the program rather than creating an enrollment out of nothing.
  const createHeading = fromWaitlist
    ? t('enrollmentsBilling.createModal.moveToProgram')
    : t('enrollmentsBilling.createModal.createEnrollment')
  const heading = isMarkAsPaid
    ? t('enrollmentsBilling.createModal.markAsPaid')
    : isEdit ? t('enrollmentsBilling.createModal.editEnrollment') : createHeading
  const offlineMethods = [
    { value: 'cash', label: t('enrollmentsBilling.createModal.methodCash') },
    { value: 'member_charge', label: t('enrollmentsBilling.createModal.methodMemberCharge') },
    { value: 'check', label: t('enrollmentsBilling.createModal.methodCheck') },
    { value: 'custom', label: t('enrollmentsBilling.createModal.methodCustom') },
  ]
  // Only Create offers the explicit "No Package" option; Edit lists real packages only.
  const packageOptions = [
    ...(isEdit ? [] : [{ label: t('enrollmentsBilling.createModal.noPackageOption'), value: NO_PACKAGE }]),
    ...packages.map(pkg => ({ label: pkg.name, value: pkg.id })),
  ]
  const selectedPackageOption = packageOptions.find(opt => String(opt.value) === String(form.packageId)) || null
  const lineItems = invoice?.lineItems || []
  const readOnlyPackageName = lineItems.filter(item => item.lineType !== 'discount').map(item => item.description).join(', ')
  const discountItem = lineItems.find(item => item.lineType === 'discount')
  const readOnlyDiscount = discountItem ? money(Math.abs(Number(discountItem.amount)), currency) : ''

  return (
    <div className={classes.form}>
      <h2 className={classes.heading}>{heading}</h2>

      <span className={classes.label}>{t('enrollmentsBilling.createModal.choosePackage')}</span>
      {isMarkAsPaid
        ? <Input name='package' type='text' value={readOnlyPackageName} readOnly />
        : (
          <Select
            options={packageOptions}
            value={selectedPackageOption}
            onChange={opt => update({ packageId: opt ? opt.value : '' })}
            placeholder={t('enrollmentsBilling.createModal.selectPackagePlaceholder')}
            isClearable
          />
        )}

      {showBilling && (
        <>
          <span className={classes.label}>{t('enrollmentsBilling.createModal.addDiscount')}</span>
          {isMarkAsPaid
            ? <Input name='discount' type='text' value={readOnlyDiscount} readOnly />
            : (
              <div className={classes.discountRow}>
                <Input
                  name='discountAmount'
                  type='number'
                  value={form.discountAmount}
                  onChange={e => updateDiscount({ discountAmount: e.target.value })}
                  placeholder='0'
                  classNames={{ inputContainerWrapper: classes.discountInput }}
                />
                <div className={classes.discountToggle}>
                  {[DISCOUNT_TYPES.fixed, DISCOUNT_TYPES.percent].map(type => (
                    <button
                      key={type}
                      type='button'
                      className={classNames(classes.discountToggleOption, { [classes.toggleActive]: form.discountType === type })}
                      onClick={() => updateDiscount({ discountType: type })}
                    >
                      {type === DISCOUNT_TYPES.percent ? '%' : symbol}
                    </button>
                  ))}
                </div>
              </div>
            )}
          {/* What a percentage works out to, so the coach isn't entering the discount blind. */}
          {form.discountType === DISCOUNT_TYPES.percent && resolvedDiscount > 0 && (
            <span className={classes.discountHint}>
              {t('enrollmentsBilling.createModal.discountResolved', { amount: money(resolvedDiscount, currency) })}
            </span>
          )}

          <span className={classNames(classes.label, classes.required)}>{t('enrollmentsBilling.createModal.paymentMethod')}</span>
          {/* Outstanding charges accept either method (both selectable), though Online needs a connected
              Stripe account; Mark as Paid forces a single Offline method. */}
          <label className={classNames(classes.checkRow, { [classes.disabledRow]: !canChooseOnline })}>
            <input
              className={classes.nativeInput}
              type='checkbox'
              checked={form.onlineSelected}
              disabled={!canChooseOnline}
              onChange={e => update({ onlineSelected: e.target.checked })}
            />
            <span className={classes.checkbox} />
            <span>{t('enrollmentsBilling.createModal.onlineCard')}</span>
            {!stripeAccountConnected && (
              <InfoCircleIcon
                className={classes.infoIcon}
                data-tip={t('programs.builder.paymentMethod.stripeRequiredTip')}
                aria-hidden='true'
              />
            )}
          </label>
          {!stripeAccountConnected && <Tooltip className={classes.stripeTooltip} />}
          <label className={classNames(classes.checkRow, { [classes.disabledRow]: !canChooseMethod })}>
            <input
              className={classes.nativeInput}
              type='checkbox'
              checked={form.offlineSelected}
              disabled={!canChooseMethod}
              onChange={e => update({ offlineSelected: e.target.checked })}
            />
            <span className={classes.checkbox} />
            <span>{t('enrollmentsBilling.createModal.offlineCashChecks')}</span>
          </label>

          <div className={classNames(classes.toggle, { [classes.toggleReadOnly]: isMarkAsPaid })}>
            <button
              type='button'
              className={classNames(classes.toggleOption, { [classes.toggleActive]: form.markAsPaid })}
              onClick={() => !isMarkAsPaid && update({ markAsPaid: true, onlineSelected: false, offlineSelected: true })}
              disabled={isMarkAsPaid}
            >
              {t('enrollmentsBilling.createModal.markAsPaid')}
            </button>
            <button
              type='button'
              className={classNames(classes.toggleOption, { [classes.toggleActive]: !form.markAsPaid })}
              onClick={() => !isMarkAsPaid && update({ markAsPaid: false, onlineSelected: stripeAccountConnected, offlineSelected: true })}
              disabled={isMarkAsPaid}
            >
              {t('enrollmentsBilling.createModal.markAsOutstanding')}
            </button>
          </div>

          {/* The specific offline method is only chosen when marking the charge as paid; an
              outstanding charge leaves the method unset until it's actually paid. */}
          {(isMarkAsPaid || form.markAsPaid) && (
            <>
              <div className={classes.radioGrid}>
                {offlineMethods.map(method => (
                  <label key={method.value} className={classes.radioRow}>
                    <input
                      className={classes.nativeInput}
                      type='radio'
                      name='offlineMethod'
                      value={method.value}
                      checked={form.offlineMethod === method.value}
                      onChange={() => update({ offlineMethod: method.value })}
                    />
                    <span className={classes.radioMark} />
                    <span>{method.label}</span>
                  </label>
                ))}
              </div>
              {isCustom && (
                <Input
                  name='customDescription'
                  type='text'
                  value={form.customDescription}
                  onChange={e => update({ customDescription: e.target.value })}
                  placeholder={t('enrollmentsBilling.createModal.describePayment')}
                  classNames={{ inputContainerWrapper: 'mt-2' }}
                />
              )}
            </>
          )}
          <p className={classes.helperNote}>{t('enrollmentsBilling.createModal.offlineOnlyNote')}</p>
        </>
      )}

      {showNotes && (
        <>
          <span className={classes.label}>{t('enrollmentsBilling.createModal.additionalNotes')}</span>
          <Input
            name='coachMessage'
            type='text'
            value={form.coachMessage}
            onChange={e => update({ coachMessage: e.target.value })}
            placeholder={t('enrollmentsBilling.createModal.coachMessagePlaceholder')}
          />

          <label className={classNames(classes.checkRow, classes.sendEmailRow)}>
            <input className={classes.nativeInput} type='checkbox' checked={form.sendEmail} onChange={e => update({ sendEmail: e.target.checked })} />
            <span className={classes.checkbox} />
            <span>{t('enrollmentsBilling.createModal.sendAsEmail')}</span>
          </label>
        </>
      )}
    </div>
  )
}

EnrollmentForm.propTypes = {
  form: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,
  isMarkAsPaid: PropTypes.bool,
  isEdit: PropTypes.bool,
  invoice: PropTypes.object,
  currency: PropTypes.object,
  packages: PropTypes.array,
  canChooseMethod: PropTypes.bool,
  stripeAccountConnected: PropTypes.bool,
  isCustom: PropTypes.bool,
  showBilling: PropTypes.bool,
  showNotes: PropTypes.bool,
  resolvedDiscount: PropTypes.number,
  fromWaitlist: PropTypes.bool,
}

const Row = ({ label, value, valueClass, boldLabel }) => (
  <div className={classes.detailRow}>
    <span className={classNames(classes.detailLabel, { [classes.boldLabel]: boldLabel })}>{label}</span>
    <span className={classNames(classes.detailValue, valueClass)}>{value}</span>
  </div>
)

Row.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  valueClass: PropTypes.string,
  boldLabel: PropTypes.bool,
}

// Read-only display values for the invoice preview (kept out of the component to bound its complexity).
const paymentMethodDisplay = ({ t, onlineSelected, offlineSelected, offlineMethod }) => {
  const offlineLabel = offlineMethod
    ? t('enrollmentsBilling.createModal.paymentOfflineWithMethod', { method: offlineMethodLabel(t, offlineMethod) })
    : t('enrollmentsBilling.createModal.paymentOfflineUnspecified')

  if (onlineSelected && offlineSelected) { return t('enrollmentsBilling.createModal.paymentOnlineOrOffline') }
  if (onlineSelected) { return t('enrollmentsBilling.createModal.paymentOnline') }
  if (offlineSelected) { return offlineLabel }
  return t('enrollmentsBilling.createModal.paymentUnspecified')
}

const derivePreviewInfo = ({
  t, forExistingInvoice, isMarkAsPaid, course, community, coach, onlineSelected, offlineSelected, offlineMethod, invoice, packagePrice, discount,
}) => ({
  coachName: forExistingInvoice ? (course?.coachName || '-') : (coach ? `${coach.firstName} ${coach.lastName}` : '-'),
  facility: forExistingInvoice ? (course?.communityName || '-') : (community?.name || '-'),
  programDates: course?.startDate
    ? `${moment(course.startDate).format('MM/DD/YYYY')}${course.endDate ? `-${moment(course.endDate).format('MM/DD/YYYY')}` : ''}`
    : '-',
  paymentMethodLabel: paymentMethodDisplay({ t, onlineSelected, offlineSelected, offlineMethod }),
  total: isMarkAsPaid ? Number(invoice?.total || 0) : Math.max(packagePrice - discount, 0),
})

// Right column: invoice preview. Mark-as-Paid renders the existing invoice's line items read-only;
// create/edit render a live preview from the chosen package + discount.
const InvoicePreview = ({
  isMarkAsPaid, forExistingInvoice, noPackage, students, course, community, coach,
  invoice, currency, onlineSelected, offlineSelected, offlineMethod, packageName, packagePrice, discount, discountLabel, coachMessage,
}) => {
  const { t } = useTranslation()
  const [showStudents, setShowStudents] = useState(false)
  const { coachName, facility, programDates, paymentMethodLabel, total } = derivePreviewInfo({
    t, forExistingInvoice, isMarkAsPaid, course, community, coach, onlineSelected, offlineSelected, offlineMethod, invoice, packagePrice, discount,
  })

  return (
    <div className={classes.preview}>
      {!forExistingInvoice && (
        <div className={classes.previewHeader}>
          <span>
            {t(noPackage ? 'enrollmentsBilling.createModal.enrollmentFor' : 'enrollmentsBilling.createModal.invoiceFor',
              { count: students.length })}
          </span>
          <button type='button' className={classes.viewStudents} onClick={() => setShowStudents(v => !v)}>
            {t('enrollmentsBilling.createModal.viewStudents')}
          </button>
          {showStudents && (
            <div className={classes.studentsPopover}>
              {students.map(student => (
                <div key={student.id} className={classes.studentRow}>
                  <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
                  <span className={classes.studentName}>{student.fullName || `${student.firstName} ${student.lastName}`}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className={classes.invoiceCard}>
        <span className={classes.sectionTitle}>{t('enrollmentsBilling.createModal.details')}</span>

        <div className={classes.detailRows}>
          <Row label={t('enrollmentsBilling.createModal.program')} value={course?.name || '-'} />
          <Row label={t('enrollmentsBilling.createModal.facility')} value={facility} />
          <Row label={t('enrollmentsBilling.createModal.coach')} value={coachName} />
          <Row label={t('enrollmentsBilling.createModal.programDates')} value={programDates} />
          {/* No package means no invoice, so the payment method / amounts / total don't apply yet. */}
          {!noPackage && (
            <Row label={t('enrollmentsBilling.createModal.paymentMethod')} value={paymentMethodLabel} valueClass={classes.paymentMethodValue} />
          )}
        </div>

        {!noPackage && (
          <>
            <div className={classes.amountsBox}>
              {isMarkAsPaid
                ? (invoice?.lineItems || []).map(item => (
                  <Row
                    key={item.id}
                    label={item.description || t('enrollmentsBilling.createModal.discount')}
                    value={money(Math.abs(Number(item.amount)), currency)}
                    boldLabel
                  />
                ))
                : (
                  <>
                    <Row label={packageName || t('enrollmentsBilling.createModal.package')} value={money(packagePrice, currency)} boldLabel />
                    {discount > 0 && (
                      <Row label={discountLabel || t('enrollmentsBilling.createModal.discount')} value={money(discount, currency)} boldLabel />
                    )}
                  </>
                )}
            </div>

            <div className={classes.totalRow}>
              <span>{t('enrollmentsBilling.createModal.total')}</span>
              <span>{money(total, currency)}</span>
            </div>
          </>
        )}

        {isMarkAsPaid
          ? invoice?.notes && (
            <div className={classes.notesSection}>
              <span className={classes.sectionTitle}>{t('enrollmentsBilling.createModal.additionalNotes')}</span>
              <p className={classes.notesText}>{invoice.notes}</p>
            </div>
          )
          : (
            <div className={classes.notesSection}>
              <span className={classes.sectionTitle}>{t('enrollmentsBilling.createModal.additionalNotes')}</span>
              <p className={classNames(classes.notesText, { [classes.notesPlaceholder]: !coachMessage })}>
                {coachMessage || t('enrollmentsBilling.createModal.customMessage')}
              </p>
            </div>
          )}
      </div>
    </div>
  )
}

InvoicePreview.propTypes = {
  isMarkAsPaid: PropTypes.bool,
  forExistingInvoice: PropTypes.bool,
  noPackage: PropTypes.bool,
  students: PropTypes.array,
  course: PropTypes.object,
  community: PropTypes.object,
  coach: PropTypes.object,
  invoice: PropTypes.object,
  currency: PropTypes.object,
  onlineSelected: PropTypes.bool,
  offlineSelected: PropTypes.bool,
  offlineMethod: PropTypes.string,
  packageName: PropTypes.string,
  packagePrice: PropTypes.number,
  discount: PropTypes.number,
  discountLabel: PropTypes.string,
  coachMessage: PropTypes.string,
}

CreateEnrollmentModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'markAsPaid', 'edit']),
  program: PropTypes.object,
  students: PropTypes.array,
  enrollment: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  // Opened from a waitlist: the modal confirms a move rather than creating from scratch.
  fromWaitlist: PropTypes.bool,
}

export default CreateEnrollmentModal
